import React, { useEffect } from 'react';
import { Switch, Route } from 'react-router-dom';
import { NavBarContent } from 'feature/nav/NavBar';
import ReportTabs from '../Tabs';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { get, set, size } from 'global/lib/lodash';
import realmChange from 'global/wrappers/realmChange';
import PermissionGate from 'global/components/permission-gate';
import { useUser } from 'global/hooks/useUser';
import { REALM_ADD_LISTENERS } from '../../nav/NavRealm';
import ReportsLockIcon from '@material-ui/icons/Lock';
import Tooltip from '@material-ui/core/Tooltip';
import { formatSubScore } from '../AssessmentScore/ObservationCreate/ObservationCreate';
import { userHasPermissions } from 'redux/actions/userLogin';

const AssessmentIncludes = ['realm', 'realm.realms', 'realm.parent', 'scorecards', 'authors', 'editors', 'viewers', 'media', 'realmPhotos', 'lockedBy'];

export const PScoreIncludes = 'checklist,subScores,subScores.images,subScores.realm';

const include = AssessmentIncludes.join(',');

function ReportAssessment({ match }) {
  const { me, userRealm } = useUser();
  const assessment = useRetrieveData({ url: `/assessments/${match.params.id}` }, { build: ['assessments'], transformData: (d) => d[0], params: { include } });
  const highlights = useRetrieveData(
    { url: '/report-sections' },
    { build: ['reportSections'], params: { include: 'superset,images,realm,origin,user,assessment,origin.superset,origin.images,progeny', filter: { assessment_id: match.params.id } } }
  );
  const scores = useRetrieveData(
    {
      url: '/processed-scorecard-score-values',
    },
    {
      build: ['processedScorecardScoreValues'],
      params: {
        filter: { assessment_id: match.params.id },
        include: PScoreIncludes,
      },
      transformData: (d) => {
        const scdata = {};
        d.map((di) => {
          if (size(di.subScores)) {
            //note subscores is used instead of subScores, and this should hold true throughout
            //the rest of the site. Since we can't overwrite subScores since it doens't have a setter
            di.subscores = di.subScores.map((s) => formatSubScore(s, di));
          }
          set(scdata, di.scorecardId, [...get(scdata, di.scorecardId, []), di]);
        });
        return scdata;
      },
    }
  );

  const { data, loading } = assessment;

  const userIsEditor = (() => {
    if (loading) {
      return false;
    }

    const { editors, authors } = data;
    let isAdmin = false;
    if (userHasPermissions(['manageAccount'])) {
      isAdmin = userRealm.id == get(data, 'vendorId') && userRealm.id == get(data, 'clientId');
    }
    const isEditor = (editors || []).find((e) => e.id == me.id);
    const isAuthor = (authors || []).find((e) => e.id == me.id);
    return isAdmin || isEditor || isAuthor;
  })();

  const userCanEdit = (() => {
    if (loading) {
      return false;
    }
    if (get(data, 'locked') && !get(data, 'lockedBy', []).find((u) => u.id == me.id)) {
      return false;
    }
    return userIsEditor;
  })();

  const status = get(assessment, 'data.status', 'draft');

  const canShowScores = (() => {
    if (userIsEditor) {
      return true;
    }
    if (status === 'review') {
      return get(assessment, 'data.viewers', []).find((v) => v.id == me.id);
    } else if (status === 'published') {
      return true;
    } else {
      return false;
    }
  })();

  const userCanModify = userCanEdit && status !== 'published';

  useEffect(() => {
    //need to update the assessment if assessments were added from the realm picker
    window[REALM_ADD_LISTENERS].assessment = async () => {
      await assessment.update();
    };

    return () => {
      delete window[REALM_ADD_LISTENERS].assessment;
    };
  }, []);

  return (
    <>
      {data && (
        <NavBarContent
          space={true}
          pageTitle={
            get(data, 'locked') ? (
              <>
                <Tooltip title={get(data, 'locked') ? 'Locked by: ' + get(data, 'lockedBy.0.name') : ''} placement="left">
                  <ReportsLockIcon style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />
                </Tooltip>
                {data.name}
              </>
            ) : (
              data.name
            )
          }
          link="reports/list"
        />
      )}
      <Switch>
        <Route
          exact
          path="/reports/assessment/:id"
          render={(props) => (
            <ReportTabs page={match.url} assessment={assessment} scores={scores} highlights={highlights} userCanEdit={userCanEdit} canShowScores={canShowScores} currentTabValue="info" />
          )}
        />
        <Route
          exact
          path="/reports/assessment/:id/info"
          render={(props) => (
            <ReportTabs page={match.url} assessment={assessment} scores={scores} highlights={highlights} userCanEdit={userCanEdit} canShowScores={canShowScores} currentTabValue="info" />
          )}
        />
        <Route
          exact
          path="/reports/assessment/:id/scores"
          render={(props) => (
            <ReportTabs
              page={match.url}
              assessment={assessment}
              scores={scores}
              highlights={highlights}
              userCanEdit={userCanEdit}
              userCanModify={userCanModify}
              canShowScores={canShowScores}
              currentTabValue="scores"
            />
          )}
        />
        <Route
          exact
          path="/reports/assessment/:id/highlights"
          render={(props) => (
            <ReportTabs page={match.url} assessment={assessment} scores={scores} highlights={highlights} userCanEdit={userCanEdit} canShowScores={canShowScores} currentTabValue="highlights" />
          )}
        />
        <Route
          exact
          path="/reports/assessment/:id/photos"
          render={(props) => (
            <ReportTabs
              page={match.url}
              assessment={assessment}
              scores={scores}
              highlights={highlights}
              userCanEdit={userCanEdit}
              canShowScores={canShowScores}
              assessmentId={match.params.id}
              currentTabValue="photos"
            />
          )}
        />
      </Switch>
    </>
  );
}

export default PermissionGate(realmChange(ReportAssessment));
